import { useEffect, useState } from 'react'
import { VenueDetailDto, VenueOpeningHoursSource } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import {
  DayScheduleRow,
  WEEKDAY_LABELS,
  dayScheduleRowsFromOpeningHours,
  formatDayScheduleRow,
  openingHoursFromDayScheduleRows,
} from '../../utils/venue-opening-hours'

interface VenueOpeningHoursEditorProps {
  venuePublicId: string
  venueDetail: VenueDetailDto
  onUpdated: (detail: VenueDetailDto) => void
}

const VenueOpeningHoursEditor = ({ venuePublicId, venueDetail, onUpdated }: VenueOpeningHoursEditorProps) => {
  const [rows, setRows] = useState<DayScheduleRow[]>(() =>
    dayScheduleRowsFromOpeningHours(venueDetail.customOpeningHours ?? venueDetail.googleOpeningHours)
  )
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isClearing, setIsClearing] = useState(false)

  const hasCustomOverride = Boolean(venueDetail.customOpeningHours)
  const sourceLabel =
    venueDetail.openingHoursSource === VenueOpeningHoursSource.Custom
      ? 'Manual override'
      : venueDetail.openingHoursSource === VenueOpeningHoursSource.Google
        ? 'Google Places'
        : 'Not set'

  useEffect(() => {
    setRows(dayScheduleRowsFromOpeningHours(venueDetail.customOpeningHours ?? venueDetail.googleOpeningHours))
    setSaveError(null)
  }, [venueDetail.customOpeningHours, venueDetail.googleOpeningHours, venueDetail.publicId])

  const updateRow = (day: number, patch: Partial<DayScheduleRow>) => {
    setRows(prev => prev.map(row => (row.day === day ? { ...row, ...patch } : row)))
    setSaveError(null)
  }

  const googleRowsByDay = dayScheduleRowsFromOpeningHours(venueDetail.googleOpeningHours).reduce<Record<number, DayScheduleRow>>(
    (acc, row) => {
      acc[row.day] = row
      return acc
    },
    {}
  )

  const handleLoadGoogleIntoForm = () => {
    setRows(dayScheduleRowsFromOpeningHours(venueDetail.googleOpeningHours))
    setSaveError(null)
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)

    try {
      const openingHours = openingHoursFromDayScheduleRows(rows)
      const updated = await adminService.updateVenueOpeningHours(venuePublicId, openingHours)
      onUpdated(updated)
    } catch (err) {
      console.error('Failed to save opening hours', err)
      setSaveError(err instanceof Error ? err.message : 'Failed to save opening hours.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClearOverride = async () => {
    setIsClearing(true)
    setSaveError(null)

    try {
      const updated = await adminService.updateVenueOpeningHours(venuePublicId, null)
      onUpdated(updated)
    } catch (err) {
      console.error('Failed to clear opening hours override', err)
      setSaveError('Failed to clear manual override.')
    } finally {
      setIsClearing(false)
    }
  }

  return (
    <div className="venue-opening-hours-editor">
      <div className="venue-opening-hours-editor__header">
        <h3>Opening hours</h3>
        <div className="venue-opening-hours-editor__meta">
          <span>
            Shown to users: <strong>{sourceLabel}</strong>
          </span>
          {venueDetail.openingHoursTimezone && <span>Timezone: {venueDetail.openingHoursTimezone}</span>}
        </div>
      </div>

      {venueDetail.openingHours?.weekdayDescriptions && venueDetail.openingHours.weekdayDescriptions.length > 0 && (
        <div className="venue-opening-hours-editor__effective">
          <label>Current schedule (effective)</label>
          <ul>
            {venueDetail.openingHours.weekdayDescriptions.map(line => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {venueDetail.googleOpeningHours?.weekdayDescriptions &&
        venueDetail.googleOpeningHours.weekdayDescriptions.length > 0 &&
        hasCustomOverride && (
          <div className="venue-opening-hours-editor__google-ref">
            <label>Google Places (reference)</label>
            <ul>
              {venueDetail.googleOpeningHours.weekdayDescriptions.map(line => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}

      <p className="venue-opening-hours-editor__hint">
        Set weekly hours below and save to apply a manual override. Manual hours take priority over Google. Use &quot;Clear manual
        override&quot; to revert to Google data when available. For each day, check &quot;Not set&quot; when you do not want to define
        open/close times (this is not a &quot;venue is closed&quot; flag).
      </p>

      <div className="venue-opening-hours-editor__table-wrap">
        <table className="venue-opening-hours-editor__table">
          <thead>
            <tr>
              <th>Day</th>
              <th>Google</th>
              <th>Not set</th>
              <th>24h</th>
              <th>Opens</th>
              <th>Closes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.day}>
                <td>{WEEKDAY_LABELS[row.day]}</td>
                <td className="venue-opening-hours-editor__google-cell">
                  {venueDetail.googleOpeningHours ? formatDayScheduleRow(googleRowsByDay[row.day]) : '—'}
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={row.hoursNotSet}
                    onChange={e =>
                      updateRow(row.day, {
                        hoursNotSet: e.target.checked,
                        open24Hours: e.target.checked ? false : row.open24Hours,
                      })
                    }
                    title="No open/close times for this day"
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={row.open24Hours}
                    disabled={row.hoursNotSet}
                    onChange={e => updateRow(row.day, { open24Hours: e.target.checked })}
                  />
                </td>
                <td>
                  <input
                    type="time"
                    value={row.openTime}
                    disabled={row.hoursNotSet || row.open24Hours}
                    onChange={e => updateRow(row.day, { openTime: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="time"
                    value={row.closeTime}
                    disabled={row.hoursNotSet || row.open24Hours}
                    onChange={e => updateRow(row.day, { closeTime: e.target.value })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {saveError && <div className="venue-opening-hours-editor__error">{saveError}</div>}

      <div className="venue-opening-hours-editor__actions">
        <button type="button" className="styled-button sm" onClick={handleSave} disabled={isSaving || isClearing}>
          {isSaving ? 'Saving…' : 'Save manual hours'}
        </button>
        {venueDetail.googleOpeningHours && (
          <button type="button" className="styled-button sm" onClick={handleLoadGoogleIntoForm} disabled={isSaving || isClearing}>
            Load Google into form
          </button>
        )}
        {hasCustomOverride && (
          <button type="button" className="styled-button sm" onClick={handleClearOverride} disabled={isSaving || isClearing}>
            {isClearing ? 'Clearing…' : 'Clear manual override'}
          </button>
        )}
      </div>
    </div>
  )
}

export default VenueOpeningHoursEditor
